import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { IconsModule } from '@blaze-shared/theming-data-access';

import { CustomTemplateRendererService } from '../custom-template-renderer.service';

export const CUSTOM_TEMPLATE_RENDERER_SELECTOR = 'blaze-custom-template-renderer';

@UntilDestroy()
@Component({
  selector: CUSTOM_TEMPLATE_RENDERER_SELECTOR,
  templateUrl: './custom-template-renderer.component.html',
  styleUrl: './custom-template-renderer.component.scss',
  imports: [IconsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomTemplateRendererComponent implements AfterViewInit {
  private readonly customTemplateRendererService = inject(CustomTemplateRendererService);

  readonly code = input.required<string>();
  readonly context = input<unknown>();

  readonly containerRef = viewChild<ElementRef<HTMLDivElement>>('container');

  readonly isEmpty = signal(false);
  readonly isError = signal(false);
  readonly warningMessage = computed(() => {
    if (this.isError()) {
      return 'Error rendering custom template';
    }
    if (this.isEmpty()) {
      return 'No content to render';
    }
    return '';
  });

  readonly code$ = toObservable(this.code);
  readonly context$ = toObservable(this.context);

  readonly render$ = this.code$.pipe(
    switchMap((code) => {
      this.isError.set(false);
      return this.customTemplateRendererService.compile(code).pipe(
        catchError((error) => {
          console.error('Error compiling custom template', error);
          this.isError.set(true);
          return of(null);
        }),
      );
    }),
    switchMap((template) => {
      if (!template) {
        return of(null);
      }

      return this.context$.pipe(
        switchMap((context) => {
          this.isError.set(false);
          try {
            const rendered = template(context);
            return this.customTemplateRendererService.sanitize(rendered).pipe(
              map((sanitized) => {
                this.isEmpty.set(!sanitized);
                return sanitized;
              }),
              catchError((error) => {
                console.error('Error sanitizing custom template', error);
                this.isError.set(true);
                return of(null);
              }),
            );
          } catch (error) {
            console.error('Error rendering custom template', error);
            this.isError.set(true);
            return of(null);
          }
        }),
      );
    }),
    filter((rendered) => rendered !== null),
  );

  ngAfterViewInit(): void {
    const container = this.containerRef()?.nativeElement;
    if (!container) {
      return;
    }

    const shadow = container.attachShadow({ mode: 'open' });
    this.render$.pipe(untilDestroyed(this)).subscribe((rendered) => {
      shadow.innerHTML = rendered;
    });
  }
}
